import { ViewpointBuilderFooter } from 'viewpointBuilder/components/ViewpointBuilderFooter';
import { PrimaryButtonConfig } from 'viewpointBuilder/openViewpointBuilder/getPrimaryButtonConfig';
import { ComponentTypeSelect } from 'viewpointBuilder/selectContextTab/ComponentTypeSelect';
import { DoqType, DoqWithSideContent } from '@ardoq/doq';
import { ExternalLink } from '@ardoq/link';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { Field, FormSize } from '@ardoq/forms';
import { toComponentTypeOptions } from './toComponentTypeOptions';
import { MetaModelComponentType } from '@ardoq/api-types';
import styled from 'styled-components';
import { colors } from '@ardoq/design-tokens';
import { Island, ModalBodyLayout } from '@ardoq/page-layout';
import { Box, Stack } from '@ardoq/layout';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';

type SelectComponentTypeState = {
  componentType: string | null;
  componentTypes: MetaModelComponentType[];
};

type SelectComponentTypeCommands = {
  setComponentTypeName: (componentTypeName: string) => void;
};

type SelectContextTabContentProps = {
  state: SelectComponentTypeState;
  commands: SelectComponentTypeCommands;
  closeViewpointBuilder: () => void;
  primaryButtonConfig: PrimaryButtonConfig;
  i18nLocale: I18nLocale;
};

export const SelectContextComponentTypeTabContent = ({
  state,
  commands,
  closeViewpointBuilder,
  primaryButtonConfig,
  i18nLocale,
}: SelectContextTabContentProps) => {
  return (
    <ModalBodyLayout
      footerContent={
        <ViewpointBuilderFooter
          closeViewpointBuilder={closeViewpointBuilder}
          primaryButtonConfig={primaryButtonConfig}
          i18nLocale={i18nLocale}
        />
      }
    >
      <Island
        fullHeight
        header={
          <HeaderWrapper padding="xlarge">
            <Field formSize={FormSize.DEFAULT}>
              <ComponentTypeSelect
                label={t(
                  i18nLocale,
                  'viewpointBuilder:selectContextComponentType.label',
                  'Select context component type'
                )}
                placeholder={t(
                  i18nLocale,
                  'viewpointBuilder:selectContextComponentType.placeholder',
                  'Select component type'
                )}
                componentTypeOptions={toComponentTypeOptions(
                  state.componentTypes
                )}
                selectedComponentType={state.componentType ?? ''}
                setSelectedComponentType={commands.setComponentTypeName}
                isComponentTypeSelectionDisabled={false}
                i18nLocale={i18nLocale}
              />
            </Field>
          </HeaderWrapper>
        }
      >
        <Stack justify="center" flex={1}>
          <DoqyWithHint i18nLocale={i18nLocale} />
        </Stack>
      </Island>
    </ModalBodyLayout>
  );
};

const DoqyWithHint = ({ i18nLocale }: WithI18nLocale) => {
  return (
    <DoqWithSideContent
      doqType={DoqType.WAITING}
      title={t(
        i18nLocale,
        'viewpointBuilder:selectContextComponentType.howTo.title',
        'How to create a Viewpoint'
      )}
    >
      <ul>
        <li>
          {t(
            i18nLocale,
            'viewpointBuilder:selectContextComponentType.howTo.step1',
            'Select a context component type here'
          )}
        </li>
        <li>
          {t(
            i18nLocale,
            'viewpointBuilder:selectContextComponentType.howTo.step2',
            'Next, you can select reference types to add other connected component types'
          )}
        </li>
        <li>
          {t(
            i18nLocale,
            'viewpointBuilder:selectContextComponentType.howTo.step3',
            'You can also add filters, groups, formats and labels'
          )}
        </li>
      </ul>
      <ExternalLink
        opensInNewTabLabel={t(
          i18nLocale,
          'common:labels.opensInNewTab',
          '(new tab)'
        )}
        href={KnowledgeBaseLink.VIEWPOINT_BUILDER_PHOENIX}
      >
        {t(
          i18nLocale,
          'viewpointBuilder:selectContextComponentType.howTo.learnMore',
          'Learn more about the new Ardoq Viewpoints'
        )}
      </ExternalLink>
    </DoqWithSideContent>
  );
};

const HeaderWrapper = styled(Box)`
  border-bottom: 1px solid ${colors.grey300};
`;
